// File: src/pages/ProductionProductSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Form,
  FormGroup,
  Label,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { FaPlus, FaFilter, FaPencilAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2 } from "react-feather";

// ---- API & utils from your /import layer ----
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import {
  saveProductionProductSection,
  deleteProductionProductSection,
  updateProductionProductSection,
  viewProductionProductSection,
  viewProductionProductSectionById,
  Image_URL,
} from "../import/ApiEndPoint/Api";

// ---------- tiny CSS injected ONCE (page-scoped) ----------
const injectOnce = () => {
  if (document.getElementById("pps-inline-css")) return;
  const style = document.createElement("style");
  style.id = "pps-inline-css";
  style.innerHTML = `
    .pps-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .pps-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .pps-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:0; }
    .pps-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .pps-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center; }
    .pps-table tbody tr:hover{ background:#f9fbff; }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; } .chip-delete{ background:#ec1809; }
    .chip-edit svg, .chip-delete svg{ color:#fff; }
    .pps-form-title{ font-weight:800; text-transform:uppercase; font-size:18px; letter-spacing:.02em; }
    .pps-form .form-group { margin-bottom:8px; } .pps-form label { font-size:12px; margin-bottom:4px; }
    .pps-form input, .pps-form select, .pps-form textarea { font-size:12px; }
  `;
  document.head.appendChild(style);
};

// ---------- helpers ----------
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const formatDate = (s) => (s ? String(s).split("T")[0] : "");
const slugifyId = (v) => (v || "").replace(/\s+/g, "").trim();

// safe url join
const joinUrl = (...parts) =>
  parts
    .filter(Boolean)
    .map((p, i) =>
      String(p)
        [i === 0 ? "replace" : "replaceAll"](/\/+$/g, "")
        .replace(/^\/+/g, "")
    )
    .join("/");

// ---- fetch helper: avoids CORS issue by OMITTING credentials cross-origin ----
async function fetchJSON(url, options = {}) {
  const finalHeaders = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Optional bearer token (if you store it)
  const token = localStorage.getItem("token");
  if (token && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  // Only use credentials for same-origin; omit for http://localhost:7070 (cross-origin)
  let credentials = "omit";
  try {
    const urlObj = new URL(url, window.location.origin);
    credentials =
      urlObj.origin === window.location.origin ? "same-origin" : "omit";
  } catch {
    credentials = "omit";
  }

  const res = await fetch(url, {
    method: options.method || "GET",
    headers: finalHeaders,
    body: options.body,
    credentials, // <-- key change
  });

  const ctype = res.headers.get("content-type") || "";
  const isJson = ctype.includes("application/json");
  const data = isJson ? await res.json().catch(() => ({})) : await res.text();

  if (!res.ok) {
    const msg =
      (isJson ? data?.message || data?.error : String(data)) ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

// REST wrappers using SAME URLs, but proper HTTP verbs
const api = {
  // GET /rawProduct/view-production-product-section/:database
  list: async (database) => {
    const url = joinUrl(Image_URL, viewProductionProductSection, database);
    return await fetchJSON(url, { method: "GET" });
  },

  // GET by id (support both ?id= and /:id forms)
  getById: async (id) => {
    let url =
      joinUrl(Image_URL, viewProductionProductSectionById) +
      `?id=${encodeURIComponent(id)}`;
    try {
      return await fetchJSON(url, { method: "GET" });
    } catch {
      url = joinUrl(Image_URL, viewProductionProductSectionById, id);
      return await fetchJSON(url, { method: "GET" });
    }
  },

  // POST create
  create: async (payload) => {
    const url = joinUrl(Image_URL, saveProductionProductSection);
    return await fetchJSON(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // PUT update (body contains {...payload, id})
  update: async (id, payload) => {
    const url = joinUrl(Image_URL, updateProductionProductSection);
    return await fetchJSON(url, {
      method: "PUT",
      body: JSON.stringify({ ...payload, id }),
    });
  },

  // DELETE remove (tries JSON body first, then fallback to ?id=)
  remove: async (id) => {
    const url = joinUrl(Image_URL, deleteProductionProductSection);
    try {
      return await fetchJSON(url, {
        method: "DELETE",
        body: JSON.stringify({ id }),
      });
    } catch {
      const url2 =
        joinUrl(Image_URL, deleteProductionProductSection) +
        `?id=${encodeURIComponent(id)}`;
      return await fetchJSON(url2, { method: "DELETE" });
    }
  },
};

export default function ProductionProductSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  // NEW: dropdown selection (which section)
  const [selectedSectionId, setSelectedSectionId] = useState("");

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // Create/Edit modal state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("Create"); // "Create" | "Update"
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "Active",
    id: "",
  });

  useEffect(() => injectOnce(), []);

  // ----- Define columns -----
  const baseColumns = useMemo(
    () => [
      {
        key: "status",
        header: "STATUS",
        get: (r) => r?.status,
        render: (r) => (
          <span
            style={{
              fontWeight: 700,
              color: r?.status === "Active" ? "#065f46" : "#7c2d12",
            }}
          >
            {r?.status || "-"}
          </span>
        ),
      },
      { key: "id", header: "SECTION ID", get: (r) => r?.id || "" },
      { key: "name", header: "NAME", get: (r) => r?.name || "" },
      {
        key: "description",
        header: "DESCRIPTION",
        get: (r) => r?.description || "",
      },
      {
        key: "createdAt",
        header: "CREATED",
        get: (r) => formatDate(r?.createdAt),
      },
      {
        key: "updatedAt",
        header: "UPDATED",
        get: (r) => formatDate(r?.updatedAt),
      },
    ],
    []
  );

  const actionsColumn = useMemo(
    () => ({
      key: "__actions__",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          <span
            className="chip chip-edit"
            title="Edit"
            onClick={() => openForm("Update", r?._id)}
          >
            <FaPencilAlt size={14} />
          </span>
          <span
            className="chip chip-delete"
            title="Delete"
            onClick={() => handleDelete(r?._id)}
          >
            <Trash2 size={14} />
          </span>
        </div>
      ),
    }),
    []
  );

  // Column map
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  // Selected columns (persisted)
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("ProdProdSectionCols");
      let nextKeys = [];
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
      if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
      setSelectedKeys(nextKeys);
    } catch {
      setSelectedKeys(baseColumns.map((c) => c.key));
    }
  }, [baseColumns, colMap]);

  // ----- Fetch (LIST with database in URL, GET) -----
  const reload = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const db = String(user?.database || "");
      if (!db) throw new Error("No database in localStorage userData");

      const data = await api.list(db);
      const list = Array.isArray(data?.ProductionProductSection)
        ? data.ProductionProductSection
        : [];
      setRows(list);
    } catch (err) {
      console.error("[reload] fetch error:", err);
      setRows([]);
      setErrorMsg(err?.message || "Failed to fetch.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[PPS] fetch error:", e);
        setErrorMsg(e?.message || "Unable to load product sections.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ----- Visible columns & search -----
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn];
  }, [selectedKeys, colMap, actionsColumn]);

  // filter by selected section, then search
  const filteredRows = useMemo(() => {
    let base = rows;
    if (selectedSectionId) {
      base = base.filter((r) => String(r?._id) === String(selectedSectionId));
    }
    const term = search.trim().toLowerCase();
    if (!term) return base;
    const searchCols = visibleCols.filter((c) => c.key !== "__actions__");
    return base.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols, selectedSectionId]);

  // ----- Export -----
  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols.map((c) => `"${toStr(c.get(r)).replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "ProductionProductSection");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // ----- Column chooser modal -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("ProdProdSectionCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  // ----- CRUD: open form (GET by id) -----
  const openForm = async (mode, id = null) => {
    setFormMode(mode);
    setEditId(id);
    if (mode === "Create") {
      setFormData({ name: "", description: "", status: "Active", id: "" });
      setFormOpen(true);
      return;
    }
    try {
      setSaving(true);
      const res = await api.getById(id);
      const doc =
        res?.ProductionProductSection || res?.data || res?.doc || res || {};
      setFormData({
        name: doc?.name || "",
        description: doc?.description || "",
        status: doc?.status || "Active",
        id: doc?.id || slugifyId(doc?.name || ""),
      });
      setFormOpen(true);
    } catch (err) {
      swal(err?.message || "Failed to load record");
    } finally {
      setSaving(false);
    }
  };

  // ----- CRUD: delete (DELETE) -----
  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          await api.remove(id);
          setRows((prev) => prev.filter((x) => x._id !== id));
        } catch (err) {
          swal(err?.message || "Delete failed");
        }
      }
    });
  };

  // ----- CRUD: save (POST/PUT) -----
  const submitForm = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const payload = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        id: String(formData.id || slugifyId(formData.name)),
        created_by: String(user?._id || user?.id || ""),
        database: String(user?.database || ""),
      };

      if (formMode === "Create") {
        await api.create(payload); // POST
        swal("Product section saved successfully");
      } else {
        await api.update(editId, payload); // PUT
        swal("Product section updated successfully");
      }
      await reload();
      setFormOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong";
      swal(msg);
    } finally {
      setSaving(false);
    }
  };

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="pps-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Production Product Sections{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 120px" }} />

              {/* Sections dropdown */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Label className="m-0" style={{ fontSize: 12 }}>
                  Sections
                </Label>
                <Input
                  type="select"
                  value={selectedSectionId}
                  onChange={(e) => setSelectedSectionId(e.target.value)}
                  style={{ background: "#fff" }}
                >
                  <option value="">-- All Sections --</option>
                  {rows
                    .filter((r) => (r?.status || "Active") === "Active")
                    .sort((a, b) =>
                      String(a?.name || "").localeCompare(String(b?.name || ""))
                    )
                    .map((r) => (
                      <option key={r?._id} value={r?._id}>
                        {r?.name}
                      </option>
                    ))}
                </Input>
              </div>

              {/* Search */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search sections..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <Button
                outline
                color="primary"
                onClick={() => navigate(-1)}
                title="Back"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Back
              </Button>

              {/* ALWAYS-SHOW CREATE */}
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                  fontSize: 14,
                  textTransform: "uppercase",
                }}
                onClick={() => openForm("Create")}
                title="Create Section"
              >
                <FaPlus size={10} /> Create
              </Button>

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() =>
                          handleExportXLSX("ProductionProductSection")
                        }
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No product sections found.
                <div style={{ marginTop: 12 }}>
                  <Button color="primary" onClick={() => openForm("Create")}>
                    <FaPlus size={12} /> Create Section
                  </Button>
                </div>
              </div>
            ) : (
              <div className="pps-table-wrap">
                <table className="pps-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id || r?.id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Create/Edit Modal === */}
      <Modal
        isOpen={formOpen}
        toggle={() => setFormOpen((s) => !s)}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 720 }}
      >
        <ModalHeader toggle={() => setFormOpen(false)}>
          {formMode} Product Section
        </ModalHeader>
        <ModalBody>
          <Form className="pps-form" onSubmit={submitForm}>
            <Row>
              <Col lg="6" md="6" sm="12">
                <FormGroup>
                  <Label>
                    Name <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    name="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        name: e.target.value,
                        id: p.id || slugifyId(e.target.value),
                      }))
                    }
                    placeholder="Section name"
                  />
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12">
                <FormGroup>
                  <Label>Section ID</Label>
                  <Input
                    readOnly
                    value={formData.id}
                    placeholder="Auto-derived from name"
                  />
                </FormGroup>
              </Col>
              <Col lg="12" md="12" sm="12">
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Short description"
                  />
                </FormGroup>
              </Col>
              <Col lg="12" md="12" sm="12">
                <Label className="mb-0">Status</Label>
                <div className="d-flex align-items-center gap-3">
                  <label className="m-0 d-flex align-items-center gap-1">
                    <Input
                      type="radio"
                      name="status"
                      checked={formData.status === "Active"}
                      onChange={() =>
                        setFormData((p) => ({ ...p, status: "Active" }))
                      }
                    />
                    <span>Active</span>
                  </label>
                  <label className="m-0 d-flex align-items-center gap-1">
                    <Input
                      type="radio"
                      name="status"
                      checked={formData.status === "Deactive"}
                      onChange={() =>
                        setFormData((p) => ({ ...p, status: "Deactive" }))
                      }
                    />
                    <span>Deactive</span>
                  </label>
                </div>
              </Col>
            </Row>

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 10,
              }}
            >
              <Button color="secondary" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : formMode}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* === Column Chooser Modal === */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
